import { z } from 'zod';
import { PrimaryEntity } from '../../enums/crew.js';
/**
 * Sidekick, the merchant app's AI chat: everything its API sends and stores.
 * Routes are `crewMerchantSidekickApi`; the tools it calls are the `Mcp` namespace.
 * The database tables keep their older StoreAgentConversation names.
 */
// oxlint-disable-next-line @typescript-eslint/no-namespace
export var Sidekick;
(function (Sidekick) {
    const Role = {
        User: 'User',
        Assistant: 'Assistant',
    };
    // how the chat routed questions before it ran on the MCP tools; read from older stored messages
    Sidekick.MessageIntent = {
        corsoHelp: 'corsoHelp',
        clickhouseData: 'clickhouseData',
        frontReportData: 'frontReportData',
        contactRequest: 'contactRequest',
        unknown: 'unknown',
    };
    Sidekick.UserSentiment = {
        Positive: 'Positive',
        Neutral: 'Neutral',
        Negative: 'Negative',
    };
    const sourcesSchema = z.object({
        title: z.string(),
        url: z.string(),
        category: z.string(),
    });
    const pageContextBase = z.object({
        // the page's title as the merchant sees it, e.g. "#1844"
        title: z.string(),
    });
    // what the merchant has open when they ask; ids are hints the tools still check against the store
    Sidekick.pageContextSchema = z.discriminatedUnion('kind', [
        pageContextBase.extend({
            kind: z.literal(PrimaryEntity.Return),
            id: z.number(),
        }),
        pageContextBase.extend({
            kind: z.literal(PrimaryEntity.Warranty),
            id: z.number(),
        }),
        pageContextBase.extend({
            kind: z.literal(PrimaryEntity.Shipping),
            id: z.number(),
        }),
        pageContextBase.extend({
            kind: z.literal(PrimaryEntity.Registration),
            id: z.number(),
        }),
        // the order lookup page is addressed by the platform's order id
        pageContextBase.extend({
            kind: z.literal(PrimaryEntity.Order),
            sourceOrderId: z.string(),
        }),
        pageContextBase.extend({
            kind: z.literal('Automation'),
            id: z.number(),
        }),
        pageContextBase.extend({
            kind: z.literal('Settings'),
            section: z.string(),
        }),
    ]);
    // an answer's tokens summed over its model calls, to price Sidekick per store
    Sidekick.usageSchema = z.object({
        model: z.string(),
        effort: z.enum(['low', 'medium', 'high', 'xhigh', 'max']),
        inputTokens: z.number(),
        cacheReadTokens: z.number(),
        cacheWriteTokens: z.number(),
        outputTokens: z.number(),
    });
    Sidekick.messageMetadataSchema = z
        .object({
        usage: Sidekick.usageSchema.optional(),
        sources: z.array(sourcesSchema).optional(),
        intent: z.enum(Sidekick.MessageIntent).optional(),
        // AI SDK UIMessage parts (text, tool calls and results); validated by the AI SDK, not here
        parts: z.array(z.unknown()).optional(),
        // the page a question was asked from; dropped when its kind is no longer offered, so older chats still load
        pageContext: Sidekick.pageContextSchema.optional().catch(undefined),
    })
        .passthrough();
    Sidekick.messageSchema = z.object({
        role: z.enum(Role),
        content: z.string().min(1),
        metadata: Sidekick.messageMetadataSchema.nullish(),
        userSentiment: z.enum(Sidekick.UserSentiment).nullable(),
        userComment: z.string().nullable(),
        createdOn: z.string(),
        updatedOn: z.string().optional(),
        id: z.number(),
    });
    Sidekick.feedbackSchema = Sidekick.messageSchema.pick({
        userSentiment: true,
        userComment: true,
    });
    Sidekick.chatRequestSchema = z.object({
        // the new user message; the server holds the rest of the conversation
        message: z.looseObject({
            id: z.string(),
            role: z.literal('user'),
            parts: z.array(z.unknown()),
        }),
        // answer the last stored question again, replacing the answer after it
        regenerate: z.boolean().optional(),
        // absent when the merchant dismissed it, or asked from a page with nothing to point at
        pageContext: Sidekick.pageContextSchema.optional(),
    });
    Sidekick.toUiMessage = ({ id, role, content, metadata }) => ({
        id: `${id}`,
        role: role === 'User' ? 'user' : 'assistant',
        parts: metadata?.parts ?? [
            { type: 'text', text: content },
            ...(metadata?.sources ?? []).map(({ title, url }) => ({
                type: 'source-url',
                sourceId: url,
                url,
                title,
            })),
        ],
    });
    Sidekick.conversationSchema = z.object({
        id: z.number(),
        name: z.string(),
        createdOn: z.string(),
        updatedOn: z.string().optional(),
    });
    Sidekick.CONVERSATION_NAME_MAX_LENGTH = 100;
    Sidekick.renameSchema = z.object({
        name: z.string().trim().min(1).max(Sidekick.CONVERSATION_NAME_MAX_LENGTH),
    });
    Sidekick.conversationWithMessagesSchema = Sidekick.conversationSchema.extend({
        messages: z.array(Sidekick.messageSchema),
    });
    Sidekick.defaultConversationName = 'New Conversation';
})(Sidekick || (Sidekick = {}));
//# sourceMappingURL=sidekick.js.map